/**
 * SelectionAudits — list this client's AI Selection Readiness™ audits and
 * create new ones. An audit gathers attributed public evidence about a company
 * + its competitors and runs the V7 auditor to produce a scored (Familiarity /
 * Trust / Relevance) 14-section consulting report answering "once buyers find
 * you, why would they choose you?". Audits are immutable history — re-auditing
 * a company creates a new dated run (the before/after proof substrate).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { BenchmarkRun, SelectionAudit } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

/** Map audit statuses onto the existing pill palette. */
export function auditPillClass(status: SelectionAudit['status']): string {
  switch (status) {
    case 'pending':
      return 'pill pill-pending';
    case 'complete':
      return 'pill pill-complete';
    case 'failed':
      return 'pill pill-failed';
    default:
      return 'pill pill-processing';
  }
}

/** Parse a comma-separated text input into trimmed, non-empty values. */
function parseCsvInput(raw: string, max: number): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, max);
}

export function SelectionAudits(): JSX.Element {
  const navigate = useNavigate();
  const [audits, setAudits] = useState<SelectionAudit[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New-audit form state.
  const [showForm, setShowForm] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [audience, setAudience] = useState('');
  const [competitors, setCompetitors] = useState('');
  const [categories, setCategories] = useState('');
  const [challenge, setChallenge] = useState('');
  const [benchmarkRunId, setBenchmarkRunId] = useState('');
  const [model, setModel] = useState('');
  const [webSearch, setWebSearch] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [page, benchPage] = await Promise.all([
        api.listSelectionAudits(),
        api.listBenchmarks().catch(() => null),
      ]);
      setAudits(page.items);
      if (benchPage) {
        setBenchmarks(benchPage.items.filter((b) => b.status === 'complete'));
      }
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to load audits',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!companyName.trim()) {
      setFormError('Company name is required');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const { run } = await api.createSelectionAudit({
        companyName: companyName.trim(),
        ...(companyUrl.trim() ? { companyUrl: companyUrl.trim() } : {}),
        audience: parseCsvInput(audience, 10),
        competitors: parseCsvInput(competitors, 8),
        productCategories: parseCsvInput(categories, 20),
        ...(challenge.trim() ? { challenge: challenge.trim() } : {}),
        ...(benchmarkRunId ? { benchmarkRunId } : {}),
        ...(model.trim() ? { model: model.trim() } : {}),
        webSearch,
      });
      navigate(`/selection-audits/${run.id}`);
    } catch (err) {
      setFormError(
        err instanceof ApiClientError
          ? err.message
          : 'Failed to create the audit',
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="selection-audits">
      <div className="page-head">
        <h1>AI Selection Readiness™ Audits</h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? 'Close' : 'New audit'}
        </button>
      </div>

      {showForm && (
        <form className="card" onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="sa-company">Company name *</label>
            <input
              id="sa-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Biomatik"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="sa-url">Company website</label>
            <input
              id="sa-url"
              type="url"
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
              placeholder="https://www.example.com"
            />
          </div>
          <div className="field">
            <label htmlFor="sa-audience">
              Audience / buyer groups (comma-separated)
            </label>
            <input
              id="sa-audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="Academic researchers, Biotech/Pharma researchers, Government labs"
            />
          </div>
          <div className="field">
            <label htmlFor="sa-competitors">
              Competitors reviewed (comma-separated, up to 8 — leave empty to
              let the auditor identify them)
            </label>
            <input
              id="sa-competitors"
              value={competitors}
              onChange={(e) => setCompetitors(e.target.value)}
              placeholder="GenScript, IDT, Bio-Techne / R&D Systems, LifeSpan BioSciences, MyBioSource"
            />
          </div>
          <div className="field">
            <label htmlFor="sa-categories">
              Product categories (comma-separated, optional)
            </label>
            <input
              id="sa-categories"
              value={categories}
              onChange={(e) => setCategories(e.target.value)}
              placeholder="ELISA kits, antibodies, recombinant proteins, custom gene synthesis"
            />
          </div>
          <div className="field">
            <label htmlFor="sa-challenge">
              Conversion challenge (optional)
            </label>
            <input
              id="sa-challenge"
              value={challenge}
              onChange={(e) => setChallenge(e.target.value)}
              placeholder="e.g. Conversion of website and referral traffic"
            />
          </div>
          <div className="field">
            <label htmlFor="sa-benchmark">
              Ground in a benchmark run (optional — links discoverability data
              as evidence)
            </label>
            <select
              id="sa-benchmark"
              value={benchmarkRunId}
              onChange={(e) => setBenchmarkRunId(e.target.value)}
            >
              <option value="">None</option>
              {benchmarks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.companyName} — {new Date(b.createdAt).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="sa-model">
              Auditor model (OpenRouter slug; empty = configured default)
            </label>
            <input
              id="sa-model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="openai/gpt-5.4-mini"
            />
          </div>
          <div className="filters">
            <label>
              <input
                type="checkbox"
                checked={webSearch}
                onChange={(e) => setWebSearch(e.target.checked)}
              />{' '}
              Gather web evidence (recommended)
            </label>
          </div>
          {formError && <div className="form-error">{formError}</div>}
          <div className="head-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating…' : 'Run audit'}
            </button>
          </div>
        </form>
      )}

      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <div className="muted centered">Loading audits…</div>
      ) : audits.length === 0 ? (
        <div className="empty-state muted">
          No selection audits yet. Create one to see why buyers would choose a
          company once AI surfaces it — scored on Familiarity, Trust, and
          Relevance.
        </div>
      ) : (
        <table className="leads-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Overall</th>
              <th>F / T / R</th>
              <th>Status</th>
              <th>Cost</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {audits.map((a) => (
              <tr key={a.id}>
                <td className="lead-name">{a.companyName}</td>
                <td>{a.overallScore ?? '—'}</td>
                <td>
                  {a.familiarityScore ?? '—'} / {a.trustScore ?? '—'} /{' '}
                  {a.relevanceScore ?? '—'}
                </td>
                <td>
                  <span className={auditPillClass(a.status)}>{a.status}</span>
                </td>
                <td>{a.usage ? `$${a.usage.cost.toFixed(2)}` : '—'}</td>
                <td>{new Date(a.createdAt).toLocaleDateString()}</td>
                <td>
                  <Link
                    to={`/selection-audits/${a.id}`}
                    className="btn btn-small"
                  >
                    View report
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
